import React, { useEffect, useRef, useState } from 'react'
import { logError } from '@utils/logger'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  DocumentTextIcon,
  Cog6ToothIcon,
  FolderIcon,
  SparklesIcon,
  Bars3Icon,
  XMarkIcon,
  PlusIcon,
  CloudArrowUpIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'

import { useDocumentStore } from '@stores/documentStore'
import { useEditorStore } from '@stores/editorStore'
import { useAuthStore } from '@stores/authStore'
import { useThemeStore } from '@stores/themeStore'
import { CreateDocumentModal } from '@components/document/CreateDocumentModal'
import { MoonIcon, SunIcon } from '@heroicons/react/24/outline'
// CreditBalance removed - users don't see token balance anymore
// Subscription UI temporarily disabled for free launch.
// import { SubscriptionBadge } from '@components/subscription/SubscriptionBadge'
// import { useQuery } from '@tanstack/react-query'
// import { subscriptionsAPI } from '@api/subscriptions'

interface HeaderProps {
  className?: string
  onSidebarToggle?: () => void
  sidebarOpen?: boolean
}

const Header: React.FC<HeaderProps> = ({ className, onSidebarToggle, sidebarOpen }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const [showMobileMenu, setShowMobileMenu] = useState(false)
  const [showDocumentMenu, setShowDocumentMenu] = useState(false)
  
  const { currentDocument, createDocument, saveCurrentDocument, isSaving } = useDocumentStore()
  const { isDirty } = useEditorStore()
  const { user, logout, isAuthenticated } = useAuthStore()
  const { effectiveTheme, toggleTheme } = useThemeStore()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const userMenuRef = useRef<HTMLDivElement | null>(null)

  // Fetch subscription info for badge (temporarily disabled for free launch)
  // const { data: subscriptionInfo } = useQuery({
  //   queryKey: ['subscription', 'info'],
  //   queryFn: () => subscriptionsAPI.getInfo(),
  //   enabled: isAuthenticated,
  //   refetchInterval: 60000, // Refetch every minute
  // })

  const isEditorPage = location.pathname.startsWith('/editor')
  const isHomePage = location.pathname === '/home'

  // Track whether a document is open so we can show an Editor shortcut when relevant
  const hasDocument = !!currentDocument

  // Keep the top-level navigation minimal: only a contextual Home / Editor / New Document item.
  const navigationItems: Array<{
    name: string
    href: string
    icon: React.ComponentType<{ className?: string }>
    current: boolean
    onClick?: () => void
  }> = [
    {
      name: isHomePage ? 'Home' : hasDocument ? 'Editor' : 'New Document',
      href: isHomePage ? '/home' : hasDocument ? '/editor' : '#',
      icon: hasDocument ? DocumentTextIcon : SparklesIcon,
      current: isHomePage || (isEditorPage && hasDocument),
      onClick: !isHomePage && !hasDocument ? () => setShowCreateModal(true) : undefined,
    },
  ]

  const handleNewDocument = async () => {
    try {
      const now = new Date()
      const dateStr = now.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      })
      const timeStr = now.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      })
      
      const newDoc = await createDocument({
        title: `Untitled Document - ${dateStr} at ${timeStr}`,
        content: '',
      })
      navigate(`/editor/${newDoc.id}`)
    } catch (error) {
      logError('Failed to create document:', error)
    }
  }

  // Handler for CreateDocumentModal
  const handleCreateDocument = async (title: string, documentType: string, content?: string): Promise<string> => {
    const newDoc = await createDocument({
      title,
      content: content || '',
      document_type: documentType as any,
    })
    navigate(`/editor/${newDoc.id}`)
    setShowCreateModal(false)
    return newDoc.id
  }

  const handleSave = async () => {
    if (currentDocument && isDirty) {
      await saveCurrentDocument()
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
    setShowUserMenu(false)
  }

  // Close user menu when clicking outside or when the route changes
  useEffect(() => {
    if (!showUserMenu) return

    const handleClickOutside = (event: MouseEvent) => {
      if (!userMenuRef.current) return
      if (userMenuRef.current.contains(event.target as Node)) return
      setShowUserMenu(false)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu])

  useEffect(() => {
    // Whenever the location changes, ensure the user menu is closed
    setShowUserMenu(false)
  }, [location.pathname])

  return (
    <header className={clsx("bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shadow-sm", className)}>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo and brand */}
          <div className="flex items-center gap-3">
            {/* Sidebar Toggle Button - Mobile/Tablet */}
            {onSidebarToggle && (
              <button
                onClick={onSidebarToggle}
                className="lg:hidden p-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label="Toggle sidebar"
              >
                {sidebarOpen ? (
                  <XMarkIcon className="w-6 h-6" />
                ) : (
                  <Bars3Icon className="w-6 h-6" />
                )}
              </button>
            )}
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500/10 to-cyan-500/10 dark:from-teal-500/20 dark:to-cyan-500/20 border border-teal-500/20 dark:border-teal-400/30 shadow-sm group-hover:shadow-md transition-all overflow-hidden p-1.5">
                <img 
                  src="/logos/favicon.svg" 
                  alt="SciDraft Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">
                  SciDraft
                </h1>
                <p className="text-xs text-slate-500 dark:text-slate-400">Scientific Writing Assistant</p>
              </div>
            </Link>
          </div>

          {/* Center - Document info and actions (on editor page only) */}
          {isEditorPage && !isHomePage && (
            <div className="flex items-center space-x-4">
              {/* Document title */}
              <div className="relative">
                <button
                  onClick={() => setShowDocumentMenu(!showDocumentMenu)}
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-md transition-colors"
                >
                  <span className="truncate max-w-xs">
                    {currentDocument?.title || 'Untitled Document'}
                  </span>
                  {isDirty && <span className="text-orange-500">•</span>}
                  <ChevronDownIcon className="w-4 h-4" />
                </button>

                {/* Document menu */}
                {showDocumentMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-slate-800 rounded-md shadow-lg border border-slate-200 dark:border-slate-700 z-50"
                  >
                    <div className="py-1">
                      <button
                        onClick={handleNewDocument}
                        className="flex items-center w-full px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                      >
                        <PlusIcon className="w-4 h-4 mr-3" />
                        New Document
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={!isDirty || isSaving}
                        className="flex items-center w-full px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <CloudArrowUpIcon className={clsx(
                          'w-4 h-4 mr-3',
                          isSaving && 'animate-pulse'
                        )} />
                        {isSaving ? 'Saving...' : 'Save Document'}
                      </button>
                      <div className="border-t border-slate-100 dark:border-slate-700 my-1" />
                      <Link
                        to="/documents"
                        className="flex items-center w-full px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                        onClick={() => setShowDocumentMenu(false)}
                      >
                        <FolderIcon className="w-4 h-4 mr-3" />
                        All Documents
                      </Link>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Save status */}
              {currentDocument && (
                <div className="flex items-center space-x-2 text-sm text-slate-500 dark:text-slate-400">
                  {isSaving ? (
                    <>
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
                      <span>Saving...</span>
                    </>
                  ) : isDirty ? (
                    <>
                      <div className="w-2 h-2 bg-orange-500 rounded-full" />
                      <span>Unsaved changes</span>
                    </>
                  ) : (
                    <>
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      <span>Saved</span>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Right side - Navigation and actions */}
          <div className="flex items-center space-x-4">
            {/* Subscription badge and buttons temporarily removed for free launch */}
            {/* {isAuthenticated && (
              <div className="flex items-center space-x-2">
                {subscriptionInfo && (
                  <SubscriptionBadge
                    subscriptionType={subscriptionInfo.subscription_type}
                    planName={subscriptionInfo.plan_name}
                    isActive={subscriptionInfo.is_active}
                    size="sm"
                  />
                )}
                {subscriptionInfo && subscriptionInfo.subscription_type === 'free' ? (
                  <button
                    onClick={() => navigate('/subscription')}
                    className="px-3 py-1.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors"
                  >
                    Subscribe
                  </button>
                ) : subscriptionInfo && subscriptionInfo.subscription_type !== 'free' ? (
                  <button
                    onClick={() => navigate('/subscription')}
                    className="px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
                  >
                    Manage
                  </button>
                ) : null}
              </div>
            )} */}

            {/* Dark Mode Toggle */}
            {isAuthenticated && (
              <button
                onClick={toggleTheme}
                className="p-2 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                title={effectiveTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {effectiveTheme === 'dark' ? (
                  <SunIcon className="w-5 h-5 text-slate-700 dark:text-slate-300" />
                ) : (
                  <MoonIcon className="w-5 h-5 text-slate-700 dark:text-slate-300" />
                )}
              </button>
            )}

            {/* Desktop navigation */}
            {isAuthenticated && (
              <nav className="hidden md:flex space-x-1">
                {navigationItems.map((item) => {
                  const baseClassName = clsx(
                    'flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors',
                    item.current
                      ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-300'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
                  )
                  
                  if (item.onClick) {
                    return (
                      <button
                        key={item.name}
                        onClick={item.onClick}
                        className={baseClassName}
                      >
                        <item.icon className="w-4 h-4 mr-2" />
                        {item.name}
                      </button>
                    )
                  }
                  
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={baseClassName}
                    >
                      <item.icon className="w-4 h-4 mr-2" />
                      {item.name}
                    </Link>
                  )
                })}
              </nav>
            )}

            {/* User menu */}
            {isAuthenticated && user && (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-slate-50 transition-colors"
                >
                  <UserCircleIcon className="w-5 h-5" />
                  <span className="hidden sm:block">{user.username || user.email}</span>
                  <ChevronDownIcon className="w-4 h-4" />
                </button>

                {/* User dropdown menu */}
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-slate-800 rounded-md shadow-lg border border-slate-200 dark:border-slate-700 z-50"
                  >
                    <div className="py-1">
                      <div className="px-4 py-2 border-b border-slate-100 dark:border-slate-700">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          {user.full_name || user.username}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user.email}</p>
                      </div>

                      {/* Workspace links moved here to declutter the header */}
                      <button
                        onClick={() => {
                          setShowUserMenu(false)
                          setShowCreateModal(true)
                        }}
                        className="flex items-center w-full px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                      >
                        <PlusIcon className="w-4 h-4 mr-3" />
                        New Document
                      </button>
                      <Link
                        to="/documents"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                      >
                        <FolderIcon className="w-4 h-4 mr-3" />
                        Documents
                      </Link>
                      <Link
                        to="/settings"
                        onClick={() => setShowUserMenu(false)}
                        className="flex items-center px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                      >
                        <Cog6ToothIcon className="w-4 h-4 mr-3" />
                        Settings
                      </Link>
                      {user.role === 'admin' && (
                        <Link
                          to="/admin"
                          onClick={() => setShowUserMenu(false)}
                          className="flex items-center px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                        >
                          <ShieldCheckIcon className="w-4 h-4 mr-3" />
                          Admin Panel
                        </Link>
                      )}

                      <div className="border-t border-slate-100 dark:border-slate-700 my-1" />

                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
                      >
                        <ArrowRightOnRectangleIcon className="w-4 h-4 mr-3" />
                        Sign out
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>
            )}

            {/* Login/Register buttons (when not authenticated) */}
            {!isAuthenticated && (
              <div className="flex items-center space-x-2">
                <Link
                  to="/login"
                  className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-md transition-colors"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
                >
                  Sign up
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2 rounded-md text-slate-600 hover:text-slate-900 hover:bg-slate-50"
            >
              {showMobileMenu ? (
                <XMarkIcon className="w-5 h-5" />
              ) : (
                <Bars3Icon className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {showMobileMenu && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-slate-200 dark:border-slate-700 py-4"
          >
            <nav className="space-y-1">
              {isAuthenticated ? (
                <>
                  {/* Subscription Badge and Token Balance - Mobile (temporarily hidden for free launch) */}
                  {/* <div className="px-3 py-2 space-y-2">
                    {subscriptionInfo && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600 dark:text-slate-400">Plan:</span>
                        <SubscriptionBadge
                          subscriptionType={subscriptionInfo.subscription_type}
                          planName={subscriptionInfo.plan_name}
                          isActive={subscriptionInfo.is_active}
                          size="sm"
                        />
                      </div>
                    )}
                  </div> */}
                  
                  <div className="border-t border-slate-100 dark:border-slate-700 my-1" />
                  
                  {/* Dark Mode Toggle - Mobile */}
                  <button
                    onClick={() => {
                      toggleTheme()
                      setShowMobileMenu(false)
                    }}
                    className="flex items-center w-full px-3 py-2 rounded-md text-base font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
                  >
                    {effectiveTheme === 'dark' ? (
                      <>
                        <SunIcon className="w-5 h-5 mr-3" />
                        Light Mode
                      </>
                    ) : (
                      <>
                        <MoonIcon className="w-5 h-5 mr-3" />
                        Dark Mode
                      </>
                    )}
                  </button>
                  
                  <div className="border-t border-slate-100 dark:border-slate-700 my-1" />

                  {/* Compact primary nav on mobile: same contextual item as desktop */}
                  {navigationItems.map((item) => {
                    const baseClassName = clsx(
                      'flex items-center px-3 py-2 rounded-md text-base font-medium',
                      item.current
                        ? 'bg-primary-100 text-primary-900'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                    )

                    if (item.onClick) {
                      return (
                        <button
                          key={item.name}
                          onClick={() => {
                            item.onClick?.()
                            setShowMobileMenu(false)
                          }}
                          className={baseClassName}
                        >
                          <item.icon className="w-5 h-5 mr-3" />
                          {item.name}
                        </button>
                      )
                    }

                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setShowMobileMenu(false)}
                        className={baseClassName}
                      >
                        <item.icon className="w-5 h-5 mr-3" />
                        {item.name}
                      </Link>
                    )
                  })}

                  {user && (
                    <>
                      <div className="border-t border-slate-100 dark:border-slate-700 my-1" />
                      <div className="px-3 py-2">
                        <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          {user.full_name || user.username}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user.email}</p>
                      </div>

                      {/* Workspace actions in mobile user section */}
                      <button
                        onClick={() => {
                          setShowMobileMenu(false)
                          setShowCreateModal(true)
                        }}
                        className="flex items-center w-full px-3 py-2 text-base font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-md"
                      >
                        <PlusIcon className="w-5 h-5 mr-3" />
                        New Document
                      </button>
                      <Link
                        to="/documents"
                        onClick={() => setShowMobileMenu(false)}
                        className="flex items-center px-3 py-2 text-base font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-md"
                      >
                        <FolderIcon className="w-5 h-5 mr-3" />
                        Documents
                      </Link>
                      <Link
                        to="/settings"
                        onClick={() => setShowMobileMenu(false)}
                        className="flex items-center px-3 py-2 text-base font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-md"
                      >
                        <Cog6ToothIcon className="w-5 h-5 mr-3" />
                        Settings
                      </Link>
                      {user.role === 'admin' && (
                        <Link
                          to="/admin"
                          onClick={() => setShowMobileMenu(false)}
                          className="flex items-center px-3 py-2 text-base font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-md"
                        >
                          <ShieldCheckIcon className="w-5 h-5 mr-3" />
                          Admin Panel
                        </Link>
                      )}
                      <button
                        onClick={() => {
                          handleLogout()
                          setShowMobileMenu(false)
                        }}
                        className="flex items-center w-full px-3 py-2 text-base font-medium text-red-600 hover:text-red-900 hover:bg-red-50 rounded-md"
                      >
                        <ArrowRightOnRectangleIcon className="w-5 h-5 mr-3" />
                        Sign out
                      </button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setShowMobileMenu(false)}
                    className="flex items-center px-3 py-2 rounded-md text-base font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setShowMobileMenu(false)}
                    className="flex items-center px-3 py-2 rounded-md text-base font-medium text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Sign up
                  </Link>
                </>
              )}
            </nav>
          </motion.div>
        )}
      </div>

      {/* Create Document Modal */}
      <CreateDocumentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateDocument}
      />
    </header>
  )
}

export default Header